/**
 * "Can this runtime/session ever reach the EMPLOYER (admin) surface at all?"
 *
 * Two independent reasons the answer is no, deliberately collapsed into ONE
 * derived flag so every consumer asks the same question of the same value
 * instead of each call site re-deriving "employee-only" from its own pair of
 * inputs (the drift failure mode `docs/design-system/CLAUDE.md` § "One
 * canonical component per status/badge concept" describes for badges — same
 * shape, applied to a permission-ish bit):
 *
 * 1. **A real employee (self-service) session.** An employee has no admin
 *    view to return to: their grant carries `firmId: null` and no
 *    `payroll:employer:*` scope, so every admin query would 403.
 * 2. **The native Capacitor shell (`apps/wagepro-mobile`).** WagePro on a
 *    phone is an employee app — payslips, leave, "my pay" — and the employer
 *    console (pay runs, the employee directory, statutory filing, settings)
 *    is deliberately desktop-only. The SAME `dist/` bundle serves both
 *    runtimes (CLAUDE.md § "Mobile App Architecture": the shell has no source
 *    of its own), so this is a load-time runtime branch on
 *    `lib/platform.ts`'s `isNative()`, never a build flag and never a second
 *    implementation of the app.
 *
 * Note the asymmetry with reason 1: a native EMPLOYER session is still a
 * genuine employer — their token keeps every employer scope, and the same
 * account sees the full console in a desktop browser. Only the surface is
 * withheld here, which is why this is a UI-layer concept and not an
 * authorization one. The API stays the real boundary.
 */
export interface EmployeeViewOnlyInput {
  /** `true` for a REAL employee (self-service) session — `main.tsx`'s `sessionKind`. */
  is_employee_session: boolean;
  /** `true` inside the native Capacitor shell — `lib/platform.ts`'s `isNative()`. */
  is_native: boolean;
}

/**
 * Pure so the two reasons above are unit-testable without a Capacitor
 * runtime or a booted session. Injected into `WageProStoreProvider`, which
 * defaults `is_native` to the real `isNative()`.
 */
export function resolveEmployeeViewOnly(input: EmployeeViewOnlyInput): boolean {
  return input.is_employee_session || input.is_native;
}
